import { Component, createEffect, For, Match, on, onCleanup, Show, Switch } from 'solid-js';

import styles from './Media.module.scss';
import { blossomStore, deleteMedia, fetchUsageInfo, toggleMediaSelect } from './Media.data';
import { shortDate } from 'src/utils/date';
import dayjs from 'dayjs'
import NoteContextTrigger from 'src/components/NoteContextMenu/NoteContextTrigger';
import { BlobDescriptor } from 'blossom-client-sdk';
import { fileSize, humanizeFileType } from 'src/utils/ui';
import { createStore } from 'solid-js/store';
import { openMediaContextMenu } from 'src/stores/AppStore';
import { useToastContext } from 'src/context/ToastContext/ToastContext';

import missingImage from 'assets/images/missing_image.svg';
import { cancelUpload, uploadStore } from 'src/utils/upload';
import { Progress } from '@kobalte/core/progress';

import stylesUploader from 'src/components/Uploader/Uploader.module.scss';
import ThumbVideoPlayer from './ThumbVideoPlayer';

const MediaGrid: Component<{
  items: BlobDescriptor[],
  onSelect?: (blob: BlobDescriptor) => void,
}> = (props) => {
  const toast = useToastContext();

  const [visibleItems, setVisibleItems] = createStore<string[]>([]);

  let containerRef: HTMLDivElement | undefined;

  const items = () => {
    const uploads: BlobDescriptor[] = uploadStore.uploadOrder.map(u => {
      const upload = uploadStore.uploads[u];
      return {
        uploaded: dayjs().unix(),
        type: 'upload',
        sha256: upload?.id || '',
        size: upload.file?.size || 0,
        url: upload.file?.name || 'file',
      };
    });

    return [ ...uploads, ...props.items ];
  }

  // Create intersection observer
  const observer = new IntersectionObserver(
    (entries) => {
      let visible = [ ...visibleItems ];

      entries.forEach((entry) => {
        const id = entry.target.getAttribute('data-id');
        if (!id) return;

        if (entry.isIntersecting) {
          visible.push(id);
        } else {
          visible = visible.filter(i => i !== id);
        }
      });

      setVisibleItems(() => [...visible]);
    },
    {
      rootMargin: '400px 0px 400px 0px',
    }
  );

  onCleanup(() => {
    observer?.disconnect();
  });

  createEffect(on(() => [visibleItems.length, blossomStore.server], (change) => {
    const len = change[0];
    const server = change[1];

    if (!len || !server) return;

    const vi = [ ...visibleItems ];

    const storedUrls = blossomStore.usageInfo.urls;

    const urls = (vi as string[]).reduce<string[]>((acc, id) => {
      const blob = blossomStore.media[(server as string)].find(b => b.sha256 === id);
      if (!blob || storedUrls.includes(blob.url)) return acc;

      return [ ...acc, blob.url ];
    }, []);

    if (urls.length === 0) return;

    fetchUsageInfo(urls);
  }));

  createEffect(() => {
    if (!containerRef) return;

    const bls = items();

    setTimeout(() => {
      for(let i=0; i< bls.length; i++) {
        const id = bls[i].sha256;
        const el = containerRef.querySelector(`[data-id="${id}"]`);
        el && observer.observe(el);
      }

    }, 100)
  });

  const isNewMonth = (blob: BlobDescriptor, index: number) => {
    const lastBlobTime = items()[index - 1]?.uploaded || 0;

    const lbDate = dayjs.unix(lastBlobTime);
    const date = dayjs.unix(blob.uploaded);

    const isNew =
      (blossomStore.sort === 'latest' || blossomStore.sort === 'oldest') &&
      (date.month() !== lbDate.month() || date.year() !== lbDate.year());

    return isNew;
  }

  const onImgError = async (event: any) => {
    const image = event.target;

    image.onerror = "";
    image.src = missingImage;
    return true;
  };

  const onContextMenuTrigger = (
    blob: BlobDescriptor,
    contextMenu: HTMLDivElement | undefined,
  ) => {
    openMediaContextMenu(
      blob,
      contextMenu?.getBoundingClientRect(),
      () => {},
      () => {
        deleteMedia(blob.sha256).then(isDeleted => {
          toast?.sendSuccess(isDeleted ? 'File deleted' : 'Failed to delete file');
        });
      },
    );
  };

  const itemClass = (sha: string) => {
    let k = styles.item;

    if (blossomStore.selectedMedia.includes(sha)) {
      k += ` ${styles.selected}`;
    }

    if (!visibleItems.find(s => s === sha)) {
      k += ` ${styles.hidden}`;
    }

    return k;
  }

  const videoThumbnail = (url: string) => {
    return blossomStore.thumbnails[url];
  }

  return (
    <div
      class={styles.mediaListGrid}
      ref={containerRef}
    >
      <For each={items()}>
        {(blob, index) => {

          let contextMenu: HTMLDivElement | undefined;

          return (
            <>
              <Show when={isNewMonth(blob, index())}>
                <div class={styles.monthCaption}>
                  {dayjs.unix(blob.uploaded).format('MMMM YYYY')}
                </div>
              </Show>
              <div
                data-id={blob.sha256}
                class={itemClass(blob.sha256)}
                onClick={(e: MouseEvent) => {
                  e.preventDefault();
                  if (props.onSelect) {
                    props.onSelect(blob);
                    return
                  }
                  toggleMediaSelect(blob);
                }}
              >
                <Switch>
                  <Match when={blob.type === 'upload'}>
                    <div
                      class={styles.cancelUpload}
                      onClick={() => {
                        cancelUpload(blob.sha256);
                        toast?.sendInfo('Upload canceled')
                      }}
                    >
                      <div class={styles.closeIcon}></div>
                    </div>
                    <div class={`${styles.itemFooter} ${styles.progress}`}>
                      <Progress
                        value={uploadStore.uploads[blob.sha256]?.progress || 0}
                        class={stylesUploader.uploadProgress}
                      >
                        <div class={stylesUploader.progressTrackContainer}>
                          <Progress.Track class={stylesUploader.progressTrack}>
                            <Progress.Fill
                              class={`${stylesUploader.progressFill}`}
                            />
                          </Progress.Track>
                        </div>
                      </Progress>
                    </div>
                  </Match>
                  <Match when={blob.type?.includes('image/')}>
                    <img
                      src={blob.url}
                      title={shortDate(blob.uploaded)}
                      onerror={onImgError}
                    />
                    <div class={styles.itemFooter}>
                      <div class={styles.mediaSize}>{fileSize(blob.size)}</div>
                      <div class={styles.mediaType}>{humanizeFileType(blob.type)}</div>
                      <div class={styles.mediaContextHolder}>
                        <div class={styles.mediaContext}>
                          <NoteContextTrigger
                            ref={contextMenu}
                            onClick={(e: MouseEvent) => {
                              onContextMenuTrigger(blob, contextMenu);
                            }}
                            collapsed={true}
                          />
                        </div>
                      </div>
                    </div>
                  </Match>
                  <Match when={blob.type?.includes('video/')}>
                    <div class={styles.videoIcon}></div>
                    <div class={styles.videoHolder}>
                      <ThumbVideoPlayer
                        blob={blob}
                      />
                    </div>
                    <div class={styles.itemFooter}>
                      <div class={styles.mediaSize}>{fileSize(blob.size)}</div>
                      <div class={styles.mediaType}>{humanizeFileType(blob.type)}</div>
                      <div class={styles.mediaContextHolder}>
                        <div class={styles.mediaContext}>
                          <NoteContextTrigger
                            ref={contextMenu}
                            onClick={() => {
                              onContextMenuTrigger(blob, contextMenu);
                            }}
                            collapsed={true}
                          />
                        </div>
                      </div>
                    </div>
                  </Match>
                  <Match when={true}>
                    <div class={styles.missingFile}>{blob.type || blob.url}</div>

                    <div class={styles.itemFooter}>
                      <div class={styles.mediaSize}>{fileSize(blob.size)}</div>
                      <div class={styles.mediaType}>{humanizeFileType(blob.type)}</div>
                      <div class={styles.mediaContextHolder}>
                        <div class={styles.mediaContext}>
                          <NoteContextTrigger
                            ref={contextMenu}
                            onClick={() => {
                              onContextMenuTrigger(blob, contextMenu);
                            }}
                            collapsed={true}
                          />
                        </div>
                      </div>
                    </div>
                  </Match>
                </Switch>
              </div>
            </>
          )
        }}
      </For>
    </div>
  );
}

export default MediaGrid;
